import { captureException } from "@sentry/react";
import queryString from "query-string";
import { useLocation, useNavigate } from "react-router";
import { isMobile, isTablet } from "react-device-detect";
import { Klydo } from "../context/klydo/KlydoTypes";

export const useHistory = () => {
  const location = useLocation();
  const search = queryString.parse(location.search);

  const navigate = useNavigate();
  const buildPath = (
    path: string | { pathname: string; search: string },
  ): string => {
    if (typeof path === "string") return path;
    const searchUrl = path.search?.startsWith("?")
      ? path.search
      : `?${path.search}`;
    return `${path.pathname}${searchUrl}`;
  };
  const push = (
    path: string | { pathname: string; search: string },
  ): Promise<void> | void => navigate(buildPath(path), { replace: false });

  const goBack = (): void => {
    const canGoBack = history?.state?.idx > 0;
    if (!!canGoBack) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const replace = (
    path: string | { pathname: string; search: string },
  ): Promise<void> | void => navigate(buildPath(path), { replace: true });
  const setQueryParam = (param, value, pushAndNotReplace) => {
    const newSearch = { ...search };
    newSearch[param] = value;
    if (pushAndNotReplace) {
      push({
        pathname: location.pathname,
        search: queryString.stringify(newSearch),
      });
    } else {
      replace({
        pathname: location.pathname,
        search: queryString.stringify(newSearch),
      });
    }
  };
  return { push, goBack, replace, setQueryParam, search };
};

export const captureError = (error: unknown, context: object = {}) => {
  captureException(error, {
    extra: { ...context },
  });
};

export const generateRandomNumberString = () => {
  let result = "";
  for (let i = 0; i < 7; i++) {
    result += Math.floor(Math.random() * 10);
  }
  return result;
};

export const getDeviceAppropriateAnimationUrl = (
  klydo: Klydo | Partial<Klydo>,
): string => {
  if (!klydo?.klydoAnimation) {
    return klydo?.loopUrl || "";
  }

  if (isMobile) {
    return klydo?.klydoAnimation?.s;
  }

  if (isTablet) {
    return klydo?.klydoAnimation?.m;
  }

  return klydo?.klydoAnimation?.l;
};

export interface EmojiValidationResult {
  hasEmoji: boolean;
  cleanedValue: string;
  originalValue: string;
}

export const validateAndCleanEmojis = (
  value: string,
): EmojiValidationResult => {
  const emojiRanges = [
    /[\uD83C-\uDBFF\uDC00-\uDFFF]/g,
    /[\u2700-\u27BF]/g,
    /[\u2600-\u26FF]/g,
  ];

  const hasEmoji = emojiRanges.some((range) => range.test(value));

  if (hasEmoji) {
    const cleanedValue = value.replace(
      /[\uD83C-\uDBFF\uDC00-\uDFFF\u2700-\u27BF\u2600-\u26FF]/g,
      "",
    );
    return { hasEmoji: true, cleanedValue, originalValue: value };
  }

  return { hasEmoji: false, cleanedValue: value, originalValue: value };
};
